import { z } from 'zod'

// Color validation helpers
const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
const rgbaColorRegex =
	/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/

// Color schema that accepts both hex and rgba
const colorSchema = z
	.string()
	.refine(
		(value) => hexColorRegex.test(value) || rgbaColorRegex.test(value),
		'Invalid color format. Use hex (#RRGGBB) or rgba(r, g, b, a)'
	)

const questionSchema = z.object({
	id: z.string(),
	type: z.enum(['text', 'email', 'multiple_choice', 'scale']),
	question: z.string(),
	required: z.boolean(),
	options: z.array(z.string()),
	order_index: z.number().optional(),
	min_scale: z.number().optional(),
	max_scale: z.number().optional(),
})

// Define the allowed types for the primary feedback mechanism
const primaryFeedbackEnum = z.enum(['nps', 'csat', 'custom', 'testimonial'])

export const widgetFormSchema = z.object({
	// Basic Info
	name: z.string().min(1, 'Widget name is required'),

	// Module Toggles
	enable_survey: z.boolean().default(true),
	enable_bug_reporting: z.boolean().default(false),
	enable_contact_messages: z.boolean().default(false),
	enable_feature_requests: z.boolean().default(false),
	enable_reviews: z.boolean().default(false),
	enable_live_chat: z.boolean().default(false),

	// Appearance
	position: z
		.enum([
			'bottom-right',
			'bottom-left',
			'bottom-center',
			'middle-left',
			'middle-right',
		])
		.default('bottom-right'),
	primaryColor: z
		.string()
		.regex(hexColorRegex, 'Invalid hex color')
		.default('#0066FF'),
	gradientEndColor: z
		.string()
		.regex(hexColorRegex, 'Invalid hex color')
		.nullable()
		.default(null),
	backgroundColor: colorSchema.default('#FFFFFF'),
	textColor: z
		.string()
		.regex(hexColorRegex, 'Invalid hex color')
		.default('#000000'),

	// Feedback Button
	feedbackButton: z
		.object({
			show: z.boolean().default(true),
			text: z.string().default('Feedback'),
			color: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.default('#0066FF'),
			textColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.default('#FFFFFF'),
		})
		.default({
			show: true,
			text: 'Feedback',
			color: '#0066FF',
			textColor: '#FFFFFF',
		}),
	widgetTriggerSize: z.enum(['s', 'm', 'l']).default('m'),
	widgetTriggerIcon: z.string().optional(),

	// Branding
	showBranding: z.boolean().default(true),
	brandingStyle: z
		.object({
			borderColor: colorSchema.optional(),
		})
		.optional(),

	// Header
	showHeader: z.boolean().default(true),

	// Menu Item Colors
	menuItemColors: z
			.object({
				feedback_bg: colorSchema.optional(),
				feedback_icon: colorSchema.optional(),
				review_bg: colorSchema.optional(),
				review_icon: colorSchema.optional(),
				bug_bg: colorSchema.optional(),
				bug_icon: colorSchema.optional(),
				contact_bg: colorSchema.optional(),
				contact_icon: colorSchema.optional(),
				feature_bg: colorSchema.optional(),
				feature_icon: colorSchema.optional(),
			})
			.optional(),

	// Menu Item Style - Enhanced version
	menuItemStyle: z
		.object({
			layout: z.enum(['default', 'separate']).default('default'),
			background: colorSchema.optional(),
			textColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.optional(),
			iconBackgroundColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.optional(),
			iconColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.optional(),
			showDescriptions: z.boolean().default(true),
			useBorder: z.boolean().default(false),
			borderColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.optional(),
			centerAlign: z.boolean().default(false),
			verticalAlign: z.boolean().default(false),
		})
		.optional(),

	// Animation settings
	animation: z
		.object({
			header: z.enum(['none', 'subtle-gradient-shift']).default('none'),
			backgroundEffects: z.boolean().optional(),
		})
		.optional(),

	// Glassmorphism settings
	glassmorphism: z
		.object({
			enabled: z.boolean().default(false),
			backdropBlur: z.string().optional(),
			borderColor: z.string().optional(),
		})
		.optional(),

	// Layout configuration for theme-specific spacing and styling
	layout: z
		.object({
			spacing: z.enum(['compact', 'default', 'spacious']).default('default'),
			menuPadding: z.string().optional(),
			itemSpacing: z.string().optional(),
			menuColumns: z.union([z.literal(1), z.literal(2)]).optional(),
			containerBorder: z.boolean().default(true),
			headerPadding: z.string().optional(),
			headerBackground: colorSchema.optional(),
			headerTextColor: z
				.string()
				.regex(hexColorRegex, 'Invalid hex color')
				.optional(),
		})
		.optional(),

	moduleNavigation: z
		.object({
			enabled: z.boolean().optional(),
			mode: z.enum(['bottom-tabs']).optional(),
			defaultToFirstModule: z.boolean().optional(),
			modules: z
				.array(
					z.enum([
						'feedback',
						'review',
						'mailbox',
						'bug-report',
						'feature-request',
						'roadmap',
						'live-chat',
					])
				)
				.optional(),
		})
		.optional(),

	// Content
	header_title: z.string().default('How can we help you today?'),
	type: primaryFeedbackEnum.optional(),
	primary_feedback_type: primaryFeedbackEnum.default('nps'),

	// Primary Feedback Flow
	question: z.string().optional(),
	customQuestions: z
		.array(
			z.object({
				id: z.string().uuid().optional(),
				type: z.enum(['text', 'email', 'multiple_choice', 'scale']),
				question: z.string().min(1, 'Question text cannot be empty.'),
				required: z.boolean().default(false),
				options: z.array(z.string()).optional(),
				order_index: z.number().optional(),
				min_scale: z.number().optional().nullable(),
				max_scale: z.number().optional().nullable(),
			})
		)
		.optional(),

	// Thank You Message
	thankYou: z
		.object({
			title: z.string().default('Thank you!'),
			message: z.string().default('We appreciate your feedback.'),
		})
		.default({
			title: 'Thank you!',
			message: 'We appreciate your feedback.',
		}),

	// Targeting
	targeting: z
		.object({
			urlPatterns: z.array(z.string()).default([]),
			excludeUrlPatterns: z.array(z.string()).default([]),
			deviceTypes: z
				.array(z.enum(['desktop', 'mobile', 'tablet']))
				.default(['desktop', 'mobile', 'tablet']),
			userProperties: z.record(z.string()).default({}),
		})
		.default({
			urlPatterns: [],
			excludeUrlPatterns: [],
			deviceTypes: ['desktop', 'mobile', 'tablet'],
			userProperties: {},
		}),

	// Trigger
	trigger: z
		.object({
			type: z
				.enum(['immediate', 'delay', 'scroll', 'exit', 'manual'])
				.default('immediate'),
			delay: z.number().optional(),
			scrollPercentage: z.number().optional(),
		})
		.default({
			type: 'immediate',
		}),
	buttonText: z.string().default('Submit'),
})

export type CustomQuestion = z.infer<typeof questionSchema>
export type WidgetFormValues = z.infer<typeof widgetFormSchema>

export const defaultWidgetValues: WidgetFormValues = {
	name: '',
	enable_survey: true,
	enable_bug_reporting: false,
	enable_contact_messages: false,
	enable_feature_requests: false,
	enable_reviews: false,
	enable_live_chat: false,
	position: 'bottom-right',
	primaryColor: '#0066FF',
	gradientEndColor: null,
	backgroundColor: '#FFFFFF',
	textColor: '#000000',
	feedbackButton: {
		show: true,
		text: 'Feedback',
		color: '#0066FF',
		textColor: '#FFFFFF',
	},
	widgetTriggerSize: 'm',
	widgetTriggerIcon: '',
	showBranding: true,
	brandingStyle: {},
	showHeader: true,
	menuItemStyle: {
		layout: 'default',
	},
	animation: {
		header: 'none',
	},
	header_title: 'How can we help you today?',
	primary_feedback_type: 'nps',
	question: '',
	thankYou: {
		title: 'Thank you!',
		message: 'We appreciate your feedback.',
	},
	targeting: {
		urlPatterns: [],
		excludeUrlPatterns: [],
		deviceTypes: ['desktop', 'mobile', 'tablet'],
		userProperties: {},
	},
	trigger: {
		type: 'immediate',
	},
	buttonText: 'Submit',
	type: undefined,
	customQuestions: [],
}

export type UserMetadata = {
	device: string | null
	browser: string | null
	location: string | null
	pageUrl?: string | null
	language?: string | null
	referrer?: string | null
	timezone?: string | null
	pageTitle?: string | null
	timestamp?: string | null
	userAgent?: string | null
	screenSize?: {
		width: number
		height: number
	} | null
}

export type Response = {
	id: string
	widget_id: string
	team_id: string
	program_id: string | null
	score: number | null
	response_data: any
	user_metadata: UserMetadata
	created_at: string
	widgets: {
		name: string
		type: 'nps' | 'csat' | 'open' | 'custom' // Might need update if old type is removed
		question: string
	}
}
